import React, { useState, useEffect } from 'react';
import { isNil } from 'ramda';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import HTMLReactParser from 'html-react-parser';
import questionParser from 'helpers/questionParser';
import convertTagsToHTML from 'helpers/text/convertTagsToHTML';
import { addQuestion, deleteQuestion } from 'stores/questions/actions';
import Collapse from 'components/Shared/Collapse';
import SimilarQuestions from 'components/Content/SimilarQuestions';
import SpinnerOverlay from 'components/Loader/SpinnerOverlay';
import DifficultyChillis from 'components/Level/DifficultyChillis';
import {
  McqOptionsList,
  TableModal
} from 'components/Question/QuestionContent.styles';

export default function QuestionModalView({
  handleSlideTo,
  skillTitle,
  question: { VariationId }, // inconsistent question data
  questions,
  onCloseModal,
  showSolution
}) {
  const { t } = useTranslation(['browse-content']);
  const dispatch = useDispatch();
  //  State
  const [questionTitle, setQuestionTitle] = useState(null);
  const [questionSolution, setQuestionSolution] = useState(null);
  const [mcqOptions, setMCQOptions] = useState([]);

  // Redux state selector
  const { selectedTopic } = useSelector(state => state.topics);
  const { variations } = useSelector(state => state.variations);
  const { isLoading, cartQuestions, selectedQuestion } = useSelector(
    state => state.questions
  );

  // Variable definitions
  const question = selectedQuestion[VariationId] ?? {};
  const variation = variations.find(v => v.ID === VariationId) ?? {};
  const isAdded = !isNil(cartQuestions.find(item => item.ID === question.Id));
  const btnClassNames = `btn ${
    isAdded ? 'btn-outline-primary' : 'btn-primary'
  } btn-block fz14`;

  // Functions or actions
  const toggleQuestionSelector = () =>
    isAdded
      ? dispatch(deleteQuestion(question.Id))
      : dispatch(addQuestion(question));

  //  Effects
  useEffect(() => {
    if (!isNil(question.Payload)) {
      const { title, solution, mcqOptions } = questionParser(question);
      const renderOptions = mcqOptions.map(option =>
        HTMLReactParser(convertTagsToHTML(option, false), {
          replace: domNode => {
            if (domNode.name === 'img') {
              domNode.attribs.style = 'width: 68%';
              return domNode;
            }
            return domNode;
          }
        })
      );
      setMCQOptions(renderOptions);
      setQuestionTitle(
        HTMLReactParser(convertTagsToHTML(title), {
          replace: domNode => {
            if (domNode.name === 'img' && !isNil(domNode.attribs?.url)) {
              return (
                <img
                  src={domNode.attribs?.url}
                  alt={domNode.attribs?.uniqueid}
                />
              );
            }
            if (domNode.name === 'helptext') {
              return (
                <small id="questionHelp" className="form-text text-muted">
                  {domNode.children[0]?.data ?? ''}
                </small>
              );
            }
            if (domNode.name === 'input') {
              return <input type="text" />;
            }
            return domNode;
          }
        })
      );

      setQuestionSolution(HTMLReactParser(convertTagsToHTML(solution)));
    }
  }, [question]);

  return (
    <div className="question-slick">
      <SpinnerOverlay isLoading={isLoading} />
      <div className="question-item">
        <div className="question-item-header">
          <button
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={onCloseModal}
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <h3>{selectedTopic.Name}</h3>
          <p>{skillTitle}</p>
        </div>

        <div className="question-item-body">
          <div className="media">
            <div className="media-body">
              <div className="qs-inner">
                <div className="qs-attr">
                  <div className="row">
                    <div className="col-md">
                      <div className="d-flex">
                        <span data-cy="question-id">
                          {t(
                            `browse-content:questionCard.questionId`,
                            'Question ID'
                          )}
                          : {question.Id}
                        </span>
                        <span>
                          {t(
                            `browse-content:questionCard.difficulty`,
                            'Difficulty'
                          )}
                          <DifficultyChillis
                            value={question.Difficulty?.Max ?? 0}
                          />
                        </span>
                        <span className="ml-auto">{variation.Name}</span>
                      </div>
                    </div>
                    <div className="col-auto d-none">
                      <span>
                        <a href="#noredirect" data-toggle="flag">
                          <i className="icon-flag" /> Flag
                        </a>
                      </span>
                      <span>
                        <a href="#noredirect" data-toggle="love">
                          <i className="icon-heart" /> 0
                        </a>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="qs-text">
                  <TableModal>{questionTitle}</TableModal>
                  {mcqOptions.length > 0 && (
                    <McqOptionsList>
                      {mcqOptions.map(option => (
                        <li key={option}>{option}</li>
                      ))}
                    </McqOptionsList>
                  )}
                </div>
              </div>
              <div className="qs-solution">
                <Collapse
                  collapsed={showSolution}
                  hideText={t(
                    `browse-content:questionModal.hideSol`,
                    'Hide Solution'
                  )}
                  showText={t(
                    `browse-content:questionModal.showSol`,
                    'Show Solution'
                  )}
                >
                  {questionSolution}
                </Collapse>
              </div>
            </div>
            <div className="media-right">
              <div className="inner">
                <button
                  onClick={toggleQuestionSelector}
                  type="button"
                  className={btnClassNames}
                  data-cy="select-qn"
                >
                  {isAdded
                    ? t(`browse-content:questionCard.unselect`, 'Unselect')
                    : t(`browse-content:questionCard.pselect`, '+ Select')}
                </button>
                <SimilarQuestions
                  align="right"
                  button
                  onSelectedQuestion={handleSlideTo}
                  questions={questions}
                  variation={variation}
                />
                <div className="qs-tags d-none">
                  <h4>Tag</h4>
                  <ul>
                    <li>
                      <a href="#noredirect">Easy (A01)</a>
                    </li>
                    <li>
                      <a href="#noredirect">Short Answer</a>
                    </li>
                    <li>
                      <a href="#noredirect">MS</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
